import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../store/store";
import { searchTuitionApplications, fetchTuitionOptions } from "../../../../store/slices/Tuition/tuitionAdminSlice";
import Table from "../../../../components/common/Table/Table";
import Pagination from "../../../../components/common/Table/Pagination";

import { formatDateYMDToMDY } from "../../../../utils/date";
import { trackButtonClick } from '../../../../services/analytics';
import { safeGet } from "../../../../utils/safeGet";
import TuitionEmailForm from "./TuitionEmailForm";
import Loader from "../../../../components/common/Loader";

// Helper function to parse email filter value and return config
const getEmailConfig = (filterValue: string): { to: string[]; cc: string[] } => {
  const normalizedValue = filterValue.toLowerCase().replaceAll(/\s+/g, '');
  
  if (normalizedValue === 'employee') {
    return { to: ["empHomeEmail", "empWorkEmail"], cc: [] };
  } else if (normalizedValue === 'manager') {
    return { to: ["mgrEmail", "altEmail"], cc: [] };
  } else if (normalizedValue.includes('employee') && normalizedValue.includes('cc:manager')) {
    return { to: ["empHomeEmail", "empWorkEmail"], cc: ["mgrEmail", "altEmail"] };
  } else if (normalizedValue.includes('manager') && normalizedValue.includes('cc:employee')) {
    return { to: ["mgrEmail", "altEmail"], cc: ["empHomeEmail", "empWorkEmail"] };
  }
  
  return { to: [], cc: [] };
};

const collectEmailsFromRow = (row: any, fields: string[]): string[] => {
  return fields
    .map(field => row[field])
    .filter((email): email is string => typeof email === 'string' && email.trim() !== '')
    .map(email => email.trim());
};

// ==================== TYPES ====================
interface DetailsTableProps {
  searchResults: any[];
  onViewDetails: (rowData: any) => void;
  onCheckedRowsChange?: (checkedRows: string[]) => void;
  searchParams?: any;
  // When this value changes, clear selection in the table
  clearSelectionTrigger?: number;
  // Email form props
  showEmailForm?: boolean;
  selectedFilterValue?: string;
  checkedRowIds?: string[];
  emailOptions?: {
    emails: Array<{
      id: number;
      code: string;
      value: string;
    }>;
  } | null;
  onCancelEmail?: () => void;
  applicationData?: {
    applicationId: string;
    employeeNuId?: string;
  };
 
}

interface TableRow {
  [key: string]: React.ReactNode;
}

const DetailsTable = ({ 
  searchResults, 
  onViewDetails, 
  onCheckedRowsChange, 
  searchParams, 
  clearSelectionTrigger,
  showEmailForm = false,
  selectedFilterValue = "",
  checkedRowIds = [],
  emailOptions = null,
  onCancelEmail,
  applicationData
  
}: DetailsTableProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { totalRecords, currentPage, pageSize, applications, regionOptions,loading } = useSelector((state: RootState) => state.tuitionAdmin);
  const [checkedRowsData, setCheckedRowsData] = useState<any[]>([]);
  const [checkedRows, setCheckedRows] = useState<string[]>([]);
  const [applicationsRows, setApplicationsRows] = useState<any[]>([]);
  const [viewingApplicationId, setViewingApplicationId] = useState<string | null>(null);


  // Handler to clear checked rows
  const handleUncheckRows = () => {
    setCheckedRows([]);
    setCheckedRowsData([]);
  };
  useEffect(() => {
    setViewingApplicationId(null);
  }, [searchResults, applications]);

  // Ensure region options are available for ID->name mapping
  useEffect(() => {
    if (!Array.isArray(regionOptions) || regionOptions.length === 0) {
      // Fetch region options for Tuition (applType AT001)
      dispatch(fetchTuitionOptions({ type: "region", applType: "AT001" }));
    }
  }, [regionOptions, dispatch]);

  // Track searchParams changes
  useEffect(() => {
    // searchParams tracking for debugging
  }, [searchParams]);

  // Add click event listener to prevent default on pagination links
  useEffect(() => {
    const handlePaginationClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest(".ds-pagination__link")) {
        e.preventDefault();
      }
    };

    document.addEventListener("click", handlePaginationClick, true);
    return () => {
      document.removeEventListener("click", handlePaginationClick, true);
    };
  }, []);

  // Notify parent component whenever checkedRows changes
  useEffect(() => {
    if (onCheckedRowsChange) {
      onCheckedRowsChange(checkedRows);
    }
  }, [checkedRows, onCheckedRowsChange]);

  // Clear selection when trigger changes
  useEffect(() => {
    if (clearSelectionTrigger !== undefined) {
      setCheckedRows([]);
      setCheckedRowsData([]);
    }
  }, [clearSelectionTrigger]);

  // Update applicationsRows when Redux applications state changes (for pagination)
  useEffect(() => {
    const dataToMap = applications.length > 0 ? applications : searchResults;

    const mappedRows = dataToMap.length > 0
      ? dataToMap.map((item: any) => {
          // Map region ID to display name using regionOptions from dropdown
          const regionId = item.regionId ?? "";
          let regionName: string = "";
          if (Array.isArray(regionOptions)) {
            const match = regionOptions.find((opt: any) => String(opt?.id) === String(regionId));
            regionName = safeGet(match?.value, safeGet(match?.name, regionName));
          }

          return {
            employeeId: safeGet(item.empId, ""),
            employeeNuId: safeGet(item.empNuid, ""),
            lastname: safeGet(item.empLastName, ""),
            middleName:safeGet(item.empMiddleName, ""),
            firstname: safeGet(item.empFirstName, ""),
            applicationId: safeGet(item.applictnId, ""),
            applRegTxnId: safeGet(item.applRegTxnId, safeGet(item.applictnId, "")),
            region: regionName,
            programType: safeGet(item.progType, ""),
            termBeginDate: formatDateYMDToMDY(safeGet(item.termBegin, "")),
            empUploadFlag: safeGet(item.empUploadFlag, ""),
            status: safeGet(item.appStatus, ""),
            originalStatus: safeGet(item.appStatus, ""),
            action: "View details",
            empHomeEmail: safeGet(item.empHomeEmail, ""),
            empWorkEmail: safeGet(item.empWorkEmail, ""),
            mgrEmail: safeGet(item.mgrEmail, ""),
            altEmail: safeGet(item.altEmail, ""),
            unionId: safeGet(item.unionId, ""),
            unionName: safeGet(item.unionName, ""),
            applDate: formatDateYMDToMDY(safeGet(item.applDate, "")),
          };
        })
      : [];

    setApplicationsRows(mappedRows);
  }, [applications, searchResults, regionOptions]);

  // ==================== COMPUTED VALUES ====================
  const filteredRows = applicationsRows;

  const columns = [
    {
      key: "checkbox",
      label: (
        <div className="flex items-center justify-center h-full py-2!">
          <input
            type="checkbox"
            checked={checkedRows.length === filteredRows.length && filteredRows.length > 0}
            className="scale-75 cursor-pointer"
            onChange={(e) => {
              const isChecked = e.target.checked;
              const rowIds=isChecked ? filteredRows.map((row) => row.applicationId) : [];
              rowIds.forEach((rowId) => handleCheckboxChange(rowId, isChecked));
              setCheckedRows(
                isChecked ? filteredRows.map((row) => row.applicationId) : []
              );
            }}
          />
        </div>
      ),
    },
    { key: "status", label: "Status", className: "align-middle!" },
    {
      key: "applicationId",
      label: "Application ID",
      className: "align-middle!",
    },
    { key: "lastname", label: "Last name", className: "align-middle!" },
    { key: "firstname", label: "First name", className: "align-middle!" },
    { key: "employeeNuId", label: "Employee NUID", className: "align-middle!" },
    { key: "employeeId", label: "Employee ID", className: "align-middle!" },
    { key: "region", label: "Region", className: "align-middle!" },
    { key: "programType", label: "Program Type", className: "align-middle!" },
    {
      key: "termBeginDate",
      label: "Term Begin Date",
      className: "align-middle!",
    },
    {
      key: "empUploadFlag",
      label: "Employee Upload Flag",
      className: "align-middle!",
    },
    { key: "action", label: "Action", className: "align-middle!" },
  ];

  // Helper for adding checked row data
  const addCheckedRowData = (prevData: any[], rowId: string, applicationsRows: any[]) => {
    const rowData = applicationsRows.find(app => String(app.applicationId) === String(rowId));
    if (rowData && !prevData.some(app => String(app.applicationId) === String(rowId))) {
      return [...prevData, rowData];
    }
    return prevData;
  };

  // Helper for removing checked row data
  const removeCheckedRowData = (prevData: any[], rowId: string) => {
    return prevData.filter(app => String(app.applicationId) !== String(rowId));
  };

  const handleCheckboxChange = (rowId: string, isChecked: boolean) => {
    setCheckedRows((prev) => {
      const updated = isChecked
        ? [...prev, rowId]
        : prev.filter((id) => id !== rowId);
      setCheckedRowsData((prevData) => 
        isChecked 
          ? addCheckedRowData(prevData, rowId, applicationsRows)
          : removeCheckedRowData(prevData, rowId)
      );
      return updated;
    });
  };

  const handleActionClick = (rowData: TableRow) => {
      trackButtonClick('View details', 'tuition-admin-details-table', { applicationId: rowData.applicationId as string })
      // trackCustomPageView(`Tuition Admin Details - Application ${rowData.applicationId}`)
    setViewingApplicationId(rowData.applicationId as string);
    onViewDetails(rowData);
  };

  const handlePageChange = async (newPage: number) => {
    if (searchParams) {
      const params = {
        ...searchParams,
        page: newPage,
        pageSize: 10,
      };
      await dispatch(searchTuitionApplications(params));
    }
  };

  const rows = filteredRows.map((row) => {
    return {
      ...row,
      status: (
        <span className="bg-gray-100 border border-[#00000014] rounded px-3! py-1 text-sm font-semibold inline-flex items-center justify-center">
          {row.status}
        </span>
      ),
      action: (
        <button
          className={`button ${
            viewingApplicationId === row.applicationId
              ? "bg-[#003B71]! text-white!"
              : "-primary"
          }`}
          onClick={() => handleActionClick(row)}
        >
          {viewingApplicationId === row.applicationId ? "Viewing" : "View details"}
        </button>
      ),
      checkbox: (
        <div className="flex items-center justify-center h-full py-5!">
          <input
            type="checkbox"
            checked={checkedRows.includes(row.applicationId)}
            className="scale-75 cursor-pointer"
            onChange={(e) => handleCheckboxChange(row.applicationId, e.target.checked)}
          />
        </div>
      ),
    };
  });

  const renderContent = () => {
    if (showEmailForm) {
      // Get email field config based on filter value
      const emailConfig = getEmailConfig(selectedFilterValue);

      const applicationInfo = checkedRowIds.map(rowId => {
        const originalRow = checkedRowsData.find(app => String(app.applicationId) === String(rowId));
        return {
          applictnId: safeGet(originalRow?.applictnId, rowId),
          applRegTxnId: safeGet(originalRow?.applRegTxnId, safeGet(originalRow?.applictnId, rowId)),
          to: originalRow ? collectEmailsFromRow(originalRow, emailConfig.to) : [],
          cc: originalRow ? collectEmailsFromRow(originalRow, emailConfig.cc) : []
        };
      });

      return (
        <TuitionEmailForm
          selectedFilterValue={selectedFilterValue}
          applicationInfo={applicationInfo}
          emailOptions={emailOptions}
          onCancel={safeGet(onCancelEmail, () => {})}
          applicationData={applicationData}
          onEmailSent={handleUncheckRows}
        />
      );
    }

    if (applicationsRows.length === 0) {
      return (
        <div className="text-center py-8">
          <p className="p3 -book text-gray-500">No applications found. Please search to view results.</p>
        </div>
      );
    }

    return (
      <div className="mt-[15px]!">
         {loading && (
          <div className="text-center py-10 text-gray-500">
            <Loader />
          </div>
        )}
        <style>{`
          .details-table-wrapper .flex.flex-col.w-full {
            overflow-x: visible !important;
          }
        `}</style>
        <div className="details-table-wrapper">
          <Table
            columns={columns as any}
            rows={rows}
            onActionClick={handleActionClick}
            showPagination={false}
          />
        </div>

        {totalRecords > pageSize && window.innerWidth >= 768 && (
          <Pagination
            currentPage={currentPage}
            totalItems={totalRecords}
            itemsPerPage={pageSize}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    );
  };

  return (
    <div>
      {renderContent()}
    </div>
  );
};

export default DetailsTable;